import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  GTM_CONTAINER_ID,
} from "@/lib/analytics";

function readStoredConsent() {
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (raw === "analytics" || raw === "essential") return raw;
    return null;
  } catch {
    return null;
  }
}

function injectGtmOnce() {
  if (document.getElementById("radar-gtm-bootstrap")) return;

  const inline = document.createElement("script");
  inline.id = "radar-gtm-bootstrap";
  inline.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;
  document.head.appendChild(inline);
}

/**
 * Consent-gated Google Tag Manager + themed cookie bar (RADAR design tokens).
 */
export function CookieConsentRoot({ children }) {
  const [hydrated, setHydrated] = useState(false);
  const [consent, setConsent] = useState(null);
  const gtmLoaded = useRef(false);

  useEffect(() => {
    setConsent(readStoredConsent());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (consent !== "analytics" || gtmLoaded.current) return;
    gtmLoaded.current = true;
    injectGtmOnce();
  }, [consent]);

  const acceptAnalytics = useCallback(() => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, "analytics");
    } catch {
      /* ignore */
    }
    setConsent("analytics");
  }, []);

  const essentialOnly = useCallback(() => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, "essential");
    } catch {
      /* ignore */
    }
    setConsent("essential");
  }, []);

  const showBanner = hydrated && consent === null;

  return (
    <>
      <div className={showBanner ? "min-h-full pb-44 lg:pb-36" : "min-h-full"}>
        {children}
      </div>

      {showBanner ? (
        <div
          className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 shadow-[0_-8px_30px_hsl(244_30%_10%/0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-background/90"
          role="region"
          aria-label="Cookie and privacy choices"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:gap-10 lg:px-8 lg:py-8">
            <div className="min-w-0 flex-1 space-y-2">
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Privacy
              </p>
              <h2 className="font-heading text-lg font-bold tracking-tight text-foreground sm:text-xl">
                Cookies and analytics
              </h2>
              <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground font-body">
                We use cookies to keep you signed in and run Radar. If you allow
                it, we load optional tags (including analytics) via Google Tag
                Manager to understand how radar.aquivolabs.com is used. Essential
                cookies stay on either way. See our{" "}
                <a href="/privacy" className="text-primary underline-offset-4 hover:underline">
                  Privacy Policy
                </a>{" "}
                for details.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full font-heading text-xs font-semibold uppercase tracking-wide sm:w-auto"
                onClick={essentialOnly}
              >
                Essential only
              </Button>
              <Button
                type="button"
                variant="default"
                size="lg"
                className="w-full font-heading text-xs font-semibold uppercase tracking-wide shadow-elevated sm:w-auto"
                onClick={acceptAnalytics}
              >
                Accept analytics
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
